import { Button } from '@/components/chariot-ui';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useCallback, useEffect, useState } from 'react';

import { PasswordResetModal } from '@/sections/settings/user-settings';
import { formatDate } from '@/utils/date.util';

interface PasswordResetSectionProps {
  className?: string;
  disabled?: boolean;
  showLastChanged?: boolean;
  buttonLabel?: string;
  buttonStyleType?: 'primary' | 'secondary' | 'tertiary';
  buttonSize?: 'sm' | 'md' | 'lg';
  'data-testid'?: string;
}

export function PasswordResetSection({
  className = '',
  disabled = false,
  showLastChanged = true,
  buttonLabel = 'Change Password',
  buttonStyleType = 'primary',
  buttonSize = 'md',
  'data-testid': testId,
}: PasswordResetSectionProps) {
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] =
    useState(false);
  const [lastPasswordChange, setLastPasswordChange] = useState<string>(
    'Change your account password'
  );
  const [refetchCounter, setRefetchCounter] = useState(0);

  const refetchAttributes = useCallback(() => {
    setRefetchCounter(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!showLastChanged) return;

    async function fetchLastPasswordChange() {
      try {
        const attributes = await fetchUserAttributes();

        if (attributes?.updated_at) {
          const timestamp = attributes.updated_at;
          const isoString = new Date(parseInt(timestamp)).toISOString();
          const timeAgo = formatDate(isoString, undefined, true);
          setLastPasswordChange(`You last changed your password ${timeAgo}`);
        } else {
          setLastPasswordChange('No password change history available');
        }
      } catch (error) {
        console.error('Error fetching user attributes:', error);
        setLastPasswordChange('Change your account password');
      }
    }

    fetchLastPasswordChange();
  }, [refetchCounter, showLastChanged]);

  return (
    <>
      <div
        className={`flex items-center justify-between ${className}`}
        data-testid={testId}
      >
        <div className="flex flex-col gap-1">
          <h4 className="font-bold">Set New Password</h4>
          {showLastChanged ? (
            <span className="text-chariot-text-secondary text-xs">
              {lastPasswordChange}
            </span>
          ) : (
            <span className="text-chariot-text-secondary text-xs">
              Change your account password
            </span>
          )}
        </div>
        <Button
          label="Change Password"
          styleType={buttonStyleType}
          size={buttonSize}
          onClick={() => setIsPasswordResetModalOpen(true)}
          disabled={disabled}
        />
      </div>
      <PasswordResetModal
        open={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        onPasswordChange={refetchAttributes}
      />
    </>
  );
}
